import { useRef, useState } from 'react';

import { logger } from '../../core/Log/logger';
import { GeolocationBoundaries } from '../../domains/Geolocation/Geolocation';
import { Destination } from '../../resources/destination/domain/types/destination';
import { getDestinationBoundariesFromFront } from '../../resources/destinationBoundaries/api/front/destinationBoundaries';
import { NotFoundResponse } from '../../server/response/NotFoundResponse';
import { PickDomain } from '../utilities/types/PickDomain';

import type { Locale } from '../../core/i18n/Locale';

type UseDestinationBoundariesReturn = {
    getById: (destinationId: number) => GeolocationBoundaries | null;
    fetch: (
        destination: PickDomain<Destination, 'id' | 'slug'>,
        locale: Locale,
    ) => Promise<GeolocationBoundaries | null>;
    isLoading: (destinationId: number) => boolean;
};

type FetchPromisesByDestinationId = Record<number, Promise<GeolocationBoundaries | null>>;

type DestinationBoundariesById = Record<number, GeolocationBoundaries | null>;

export function useDestinationBoundaries(): UseDestinationBoundariesReturn {
    const [loadingIds, setLoadingIds] = useState<Array<number>>([]);
    const [boundariesById, setBoundariesById] = useState<DestinationBoundariesById>({});

    const pendingPromisesRef = useRef<FetchPromisesByDestinationId>({});

    function startLoading(destinationId: number) {
        setLoadingIds((previousLoadingIds) => {
            return [...previousLoadingIds, destinationId];
        });
    }

    function finishLoading(destinationId: number) {
        setLoadingIds((previousLoadingIds) => {
            return previousLoadingIds.filter((id) => {
                return id !== destinationId;
            });
        });
    }

    function isLoading(destinationId: number): boolean {
        return loadingIds.includes(destinationId);
    }

    function addBoundaries(destinationId: number, boundaries: GeolocationBoundaries | null) {
        setBoundariesById((previousBoundariesById) => {
            return {
                ...previousBoundariesById,
                [destinationId]: boundaries,
            };
        });
    }

    function addDestinationWithoutBoundaries(destinationId: number) {
        addBoundaries(destinationId, null);
    }

    function destinationBoundariesAlreadyFetched(destinationId: number): boolean {
        const destinationWithoutBoundaries = boundariesById[destinationId] === null;
        return destinationWithoutBoundaries || boundariesById[destinationId] !== undefined;
    }

    function getBoundariesById(destinationId: number): GeolocationBoundaries | null {
        return boundariesById[destinationId] ?? null;
    }

    async function fetchDestinationBoundaries(
        destination: PickDomain<Destination, 'id' | 'slug'>,
        locale: Locale,
    ): Promise<GeolocationBoundaries | null> {
        if (destinationBoundariesAlreadyFetched(destination.id)) {
            return getBoundariesById(destination.id);
        }

        const existingPromise = pendingPromisesRef.current[destination.id];
        if (existingPromise) {
            return existingPromise;
        }

        startLoading(destination.id);

        const promise = (async (): Promise<GeolocationBoundaries | null> => {
            try {
                const boundaries = await getDestinationBoundariesFromFront({
                    locale,
                    destinationSlug: destination.slug,
                });
                addBoundaries(destination.id, boundaries);
                return boundaries;
            } catch (error) {
                if (error instanceof NotFoundResponse.Error) {
                    addDestinationWithoutBoundaries(destination.id);
                    return null;
                }

                logger.error('Failed to load destination boundaries', {
                    locale,
                    destinationId: destination.id,
                    destinationSlug: destination.slug,
                    error,
                });

                return null;
            } finally {
                finishLoading(destination.id);
                delete pendingPromisesRef.current[destination.id];
            }
        })();

        pendingPromisesRef.current[destination.id] = promise;

        return promise;
    }

    return {
        getById: getBoundariesById,
        fetch: fetchDestinationBoundaries,
        isLoading,
    };
}
